import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import {
  meQueryOptions,
  noteQueryOptions,
  projectActivityQueryOptions,
  projectNotesQueryOptions,
  projectQueryOptions,
  projectsQueryOptions,
  taskQueryOptions,
  tagsQueryOptions,
  tasksQueryOptions,
  workspaceMembersQueryOptions,
  workspaceNotesQueryOptions,
  workspaceQueryOptions,
  workspaceStatusesQueryOptions,
  workspaceTasksQueryOptions,
  workspacesQueryOptions,
  type ApiClient,
} from '@fluxter/shared';
import { api, tokenStore } from '@/lib/api';
import { WorkspaceLayout } from '@/components/WorkspaceLayout';

/*
  All pages are eagerly imported into the entry bundle. We tried code-
  splitting via `React.lazy()` per route in slice 9d but reverted it:
  even with chunks aggressively prefetched on shell mount, the
  `lazy()` resolution cycle introduces a one-microtick Suspense
  hand-off when the `<Outlet />` swaps between two different lazy
  components. With `fallback={null}` that microtick reads as a brief
  blank-content flash on every navigation — the exact "page reload"
  feel we're fighting. Eager loading skips that machinery entirely:
  page components mount synchronously, with data already in the
  TanStack Query cache from the parent loader, and the content area
  swaps without any visible gap. Bundle cost is ~+90 KB minified /
  ~+25 KB gzip on the entry chunk versus the lazy variant; still
  smaller than pre-slice-9d (since the heavy shared chunks —
  `mention-suggestion`, `sortable.esm` — remain split via their
  sub-component `lazy()` calls inside editors and dnd-kit).
*/
import { HomePage } from '@/pages/HomePage';
import { LoginPage } from '@/pages/LoginPage';
import { AuthCallbackPage } from '@/pages/AuthCallbackPage';
import { CodeSentPage } from '@/pages/CodeSentPage';
import { DesktopAuthBridgePage } from '@/pages/DesktopAuthBridgePage';
import { AccessDeniedPage } from '@/pages/AccessDeniedPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { InvitationAcceptPage } from '@/pages/InvitationAcceptPage';
import { WorkspaceHomePage } from '@/pages/WorkspaceHomePage';
import { BoardPage } from '@/pages/BoardPage';
import { CalendarPage } from '@/pages/CalendarPage';
import { NoteDetailPage } from '@/pages/NoteDetailPage';
import { ProjectDetailPage } from '@/pages/ProjectDetailPage';
import { ProjectActivityPage } from '@/pages/ProjectActivityPage';
import { ProjectNotesPage } from '@/pages/ProjectNotesPage';
import { ProjectSettingsPage } from '@/pages/ProjectSettingsPage';
import { ProjectTimePage } from '@/pages/ProjectTimePage';
import { TaskDetailPage } from '@/pages/TaskDetailPage';
import { WorkspaceNotesPage } from '@/pages/WorkspaceNotesPage';
import { WorkspacePeoplePage } from '@/pages/WorkspacePeoplePage';
import { WorkspaceReportsPage } from '@/pages/WorkspaceReportsPage';
import { WorkspaceSettingsPage } from '@/pages/WorkspaceSettingsPage';
import { GithubInstallCallbackPage } from '@/pages/GithubInstallCallbackPage';

/**
 * Router context, available to every `loader` / `beforeLoad` callback as
 * `({ context }) => ...`. We expose both the React Query client (so loaders
 * can warm caches via `ensureQueryData`) and the `api` client (so loaders can
 * build `queryOptions(api, ...)` factories without going through the
 * `useApi()` hook, which only works inside React).
 */
export interface RouterContext {
  queryClient: QueryClient;
  api: ApiClient;
}

interface MeData {
  data?: {
    id?: string;
    attributes?: {
      organization_id?: string | null;
      default_workspace_id?: string | null;
    };
  };
}

interface WorkspacesData {
  data?: Array<{ id: string; attributes?: { slug?: string } }>;
}

const fetchMeOrNull = async (queryClient: QueryClient): Promise<MeData | null> => {
  try {
    const data = await queryClient.ensureQueryData(meQueryOptions(api));
    return data as MeData;
  } catch (error) {
    const status = (error as { status?: number } | null)?.status;
    if (status === 401) return null;
    throw error;
  }
};

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async ({ context }) => {
    const me = await fetchMeOrNull(context.queryClient);
    if (!me) {
      await tokenStore.clear();
      throw redirect({ to: '/login' });
    }

    const orgId = me.data?.attributes?.organization_id;
    if (!orgId) {
      throw redirect({ to: '/onboarding' });
    }

    const workspaces = (await context.queryClient.ensureQueryData(
      workspacesQueryOptions(api),
    )) as WorkspacesData;
    const defaultId = me.data?.attributes?.default_workspace_id;
    const target = workspaces.data?.find((w) => w.id === defaultId) ?? workspaces.data?.[0];

    if (target?.attributes?.slug) {
      throw redirect({
        to: '/w/$workspaceSlug',
        params: { workspaceSlug: target.attributes.slug },
      });
    }

    throw redirect({ to: '/onboarding' });
  },
  component: HomePage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: async ({ context }) => {
    // If we're already signed in with an org, send the user home.
    const token = await tokenStore.get();
    if (!token) return;
    const me = await fetchMeOrNull(context.queryClient);
    if (me?.data?.attributes?.organization_id) {
      throw redirect({ to: '/' });
    }
  },
  component: LoginPage,
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: AuthCallbackPage,
});

// Slice 1+2 own-UI: second screen of the magic-auth flow. User
// landed here from /login after submitting their email; reads the
// stashed email out of sessionStorage and prompts for the 6-digit
// code. Direct visits without a stashed email bounce to /login.
const codeSentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/code-sent',
  component: CodeSentPage,
});

// Desktop OAuth bridge. Configured in WorkOS as the redirect URI
// for desktop builds — receives the auth code from the system
// browser, forwards it to the `fluxter://` deep-link handler,
// and shows a clean "you can close this tab" message so the
// browser doesn't sit spinning on a half-loaded Google page.
const desktopAuthBridgeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/desktop-bridge',
  component: DesktopAuthBridgePage,
});

// Reached when the API's access gate rejects an authenticated email.
// AuthCallbackPage (web) / CodeSentPage (web) / LoginPage (desktop)
// navigate here when the API throws
// `api.auth.organization_membership_required`.
const accessDeniedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/access-denied',
  component: AccessDeniedPage,
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  beforeLoad: async ({ context }) => {
    const me = await fetchMeOrNull(context.queryClient);
    if (!me) {
      await tokenStore.clear();
      throw redirect({ to: '/login' });
    }
    if (me.data?.attributes?.organization_id) {
      throw redirect({ to: '/' });
    }
  },
  component: OnboardingPage,
});

/**
 * Workspace layout — renders the persistent sidebar + header chrome and
 * an `<Outlet />` for the active in-app page. Owns all data the shell
 * itself needs (workspace meta for the header, projects for the sidebar
 * list); per-page loaders below add only their own page-specific
 * queries on top.
 *
 * No `pendingComponent` here on purpose: when a user navigates between
 * sibling pages under this layout (e.g. board → calendar → notes), the
 * layout stays mounted and the shell never blinks. The `<Outlet />`
 * content area stays painted with the previous page's content until
 * the new page is ready to swap in. The `<TopProgressBar />` (rendered
 * inside `<WorkspaceLayout>`) provides the "something is loading"
 * signal during pending without disrupting the painted content.
 *
 * Cold initial navigation (login → workspace) does briefly show a
 * blank viewport before the layout paints — acceptable since the user
 * is already in a hard page transition (post-auth redirect).
 */
const workspaceLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/w/$workspaceSlug',
  loader: ({ context, params }) => {
    const { queryClient, api } = context;
    const slug = params.workspaceSlug;
    return Promise.all([
      queryClient.ensureQueryData(workspaceQueryOptions(api, slug)),
      queryClient.ensureQueryData(projectsQueryOptions(api, slug)),
    ]);
  },
  component: WorkspaceLayout,
});

const workspaceHomeRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: '/',
  loader: async ({ context, params }) => {
    const { queryClient, api } = context;
    const slug = params.workspaceSlug;
    /*
      The workspace home tasks query is filtered by `assignee_id = me.id`,
      so we need `me` resolved before kicking off `workspaceTasks`. Other
      queries run in parallel. `me` is also globally cached (5min
      staleTime) so this is usually a no-op after the first navigation.
    */
    const me = (await queryClient.ensureQueryData(meQueryOptions(api))) as MeData;
    const meId = me.data?.id;

    await Promise.all([
      queryClient.ensureQueryData(workspaceStatusesQueryOptions(api, slug)),
      queryClient.ensureQueryData(workspaceMembersQueryOptions(api, slug)),
      queryClient.ensureQueryData(
        workspaceTasksQueryOptions(api, slug, meId ? { assignee_id: meId } : {}),
      ),
    ]);
  },
  component: WorkspaceHomePage,
});

const projectRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: 'p/$projectSlug',
  loader: ({ context, params }) => {
    const { queryClient, api } = context;
    const { workspaceSlug, projectSlug } = params;
    return Promise.all([
      queryClient.ensureQueryData(projectQueryOptions(api, workspaceSlug, projectSlug)),
      queryClient.ensureQueryData(workspaceStatusesQueryOptions(api, workspaceSlug)),
      queryClient.ensureQueryData(tagsQueryOptions(api, workspaceSlug)),
      queryClient.ensureQueryData(tasksQueryOptions(api, workspaceSlug, projectSlug)),
    ]);
  },
  component: ProjectDetailPage,
});

const projectSettingsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: 'p/$projectSlug/settings',
  /*
    Project settings: only the project + members are gated. Per-member
    rate overrides are admin-gated server-side, so we leave that query
    as a component-level fetch (it returns 403 for non-admins, which the
    page would have to render an empty state for anyway).
  */
  loader: ({ context, params }) => {
    const { queryClient, api } = context;
    const { workspaceSlug, projectSlug } = params;
    return Promise.all([
      queryClient.ensureQueryData(projectQueryOptions(api, workspaceSlug, projectSlug)),
      queryClient.ensureQueryData(workspaceMembersQueryOptions(api, workspaceSlug)),
    ]);
  },
  component: ProjectSettingsPage,
});

const projectTimeRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: 'p/$projectSlug/time',
  /*
    The time-rollup query is parameterised by `range` which is component
    state initialised from "current month" — not derivable in the loader.
    The page-shape (project header) is what we preload; the rollup data
    fires under its own skeleton on first paint.
  */
  loader: ({ context, params }) => {
    const { queryClient, api } = context;
    const { workspaceSlug, projectSlug } = params;
    return queryClient.ensureQueryData(projectQueryOptions(api, workspaceSlug, projectSlug));
  },
  component: ProjectTimePage,
});

const projectBoardRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: 'p/$projectSlug/board',
  loader: ({ context, params }) => {
    const { queryClient, api } = context;
    const { workspaceSlug, projectSlug } = params;
    return Promise.all([
      queryClient.ensureQueryData(projectQueryOptions(api, workspaceSlug, projectSlug)),
      queryClient.ensureQueryData(workspaceStatusesQueryOptions(api, workspaceSlug)),
      queryClient.ensureQueryData(workspaceMembersQueryOptions(api, workspaceSlug)),
      queryClient.ensureQueryData(tagsQueryOptions(api, workspaceSlug)),
      queryClient.ensureQueryData(tasksQueryOptions(api, workspaceSlug, projectSlug)),
    ]);
  },
  component: BoardPage,
});

const projectCalendarRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: 'p/$projectSlug/calendar',
  loader: ({ context, params }) => {
    const { queryClient, api } = context;
    const { workspaceSlug, projectSlug } = params;
    return Promise.all([
      queryClient.ensureQueryData(projectQueryOptions(api, workspaceSlug, projectSlug)),
      queryClient.ensureQueryData(workspaceStatusesQueryOptions(api, workspaceSlug)),
      queryClient.ensureQueryData(workspaceMembersQueryOptions(api, workspaceSlug)),
      queryClient.ensureQueryData(tasksQueryOptions(api, workspaceSlug, projectSlug)),
    ]);
  },
  component: CalendarPage,
});

const projectNotesRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: 'p/$projectSlug/notes',
  loader: ({ context, params }) => {
    const { queryClient, api } = context;
    const { workspaceSlug, projectSlug } = params;
    return Promise.all([
      queryClient.ensureQueryData(projectQueryOptions(api, workspaceSlug, projectSlug)),
      queryClient.ensureQueryData(projectNotesQueryOptions(api, workspaceSlug, projectSlug)),
    ]);
  },
  component: ProjectNotesPage,
});

const projectActivityRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: 'p/$projectSlug/activity',
  loader: ({ context, params }) => {
    const { queryClient, api } = context;
    const { workspaceSlug, projectSlug } = params;
    return Promise.all([
      queryClient.ensureQueryData(projectQueryOptions(api, workspaceSlug, projectSlug)),
      queryClient.ensureQueryData(projectActivityQueryOptions(api, workspaceSlug, projectSlug)),
    ]);
  },
  component: ProjectActivityPage,
});

const workspaceNotesRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: 'notes',
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      workspaceNotesQueryOptions(context.api, params.workspaceSlug),
    ),
  component: WorkspaceNotesPage,
});

const noteDetailRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: 'n/$noteUlid',
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      noteQueryOptions(context.api, params.workspaceSlug, params.noteUlid),
    ),
  component: NoteDetailPage,
});

const workspaceReportsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: 'reports',
  /*
    Range-driven (component state) — no extra preload here. Layout already
    has workspace + projects warm.
  */
  component: WorkspaceReportsPage,
});

const taskRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: 't/$taskUlid',
  loader: ({ context, params }) => {
    const { queryClient, api } = context;
    const { workspaceSlug, taskUlid } = params;
    /*
      Task detail's secondary widgets — time entries, activity feed,
      sibling project tasks (subtasks picker) — are intentionally not
      load-blocked. They render under their own per-card skeletons after
      the page paints, so the user sees the title / description / status
      pills instantly and the slower aggregations stream in.
    */
    return Promise.all([
      queryClient.ensureQueryData(taskQueryOptions(api, workspaceSlug, taskUlid)),
      queryClient.ensureQueryData(workspaceStatusesQueryOptions(api, workspaceSlug)),
      queryClient.ensureQueryData(workspaceMembersQueryOptions(api, workspaceSlug)),
    ]);
  },
  component: TaskDetailPage,
});

const workspaceSettingsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: 'settings',
  loader: ({ context, params }) => {
    const { queryClient, api } = context;
    const slug = params.workspaceSlug;
    return Promise.all([
      queryClient.ensureQueryData(workspaceStatusesQueryOptions(api, slug)),
      queryClient.ensureQueryData(tagsQueryOptions(api, slug)),
    ]);
  },
  component: WorkspaceSettingsPage,
});

/**
 * Slice 11 — GitHub App install callback.
 * `fix/github-setup-url-workspace-agnostic`: lives at the **root**
 * level (no workspace slug in the path) because a single GitHub App
 * can configure only one Setup URL — that URL has to work regardless
 * of which workspace the user was on when they clicked Install.
 *
 * The page reads `installation_id` + `state` from the URL query
 * params, validates the state against `localStorage`, posts to our
 * install-callback endpoint, then navigates to `/w/{slug}/settings`
 * where `{slug}` is the workspace slug we stashed in `localStorage`
 * before opening the install URL (falls back to `/` if absent).
 *
 * Auth-required: the install flow is meaningless for unauthenticated
 * users. `beforeLoad` mirrors the index route's check — bounce to
 * `/login` if there's no valid session.
 */
const githubInstallCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/github/callback',
  beforeLoad: async ({ context }) => {
    const me = await fetchMeOrNull(context.queryClient);
    if (!me) {
      await tokenStore.clear();
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: (search: Record<string, unknown>) => ({
    installation_id: typeof search.installation_id === 'string' ? search.installation_id : undefined,
    state: typeof search.state === 'string' ? search.state : undefined,
    setup_action: typeof search.setup_action === 'string' ? search.setup_action : undefined,
  }),
  component: GithubInstallCallbackPage,
});

const workspacePeopleRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: 'people',
  /*
    Pending invitations are admin-gated server-side (non-admins 403).
    The page already renders the invitations section conditionally on
    `useMyWorkspaceMembership(slug).isAdmin`, so we leave that fetch
    component-level — non-admins should never trigger it at all.
  */
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      workspaceMembersQueryOptions(context.api, params.workspaceSlug),
    ),
  component: WorkspacePeoplePage,
});

const invitationAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/i/$invitationToken',
  component: InvitationAcceptPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  authCallbackRoute,
  codeSentRoute,
  desktopAuthBridgeRoute,
  accessDeniedRoute,
  onboardingRoute,
  workspaceLayoutRoute.addChildren([
    workspaceHomeRoute,
    projectRoute,
    projectSettingsRoute,
    projectTimeRoute,
    projectBoardRoute,
    projectCalendarRoute,
    projectNotesRoute,
    projectActivityRoute,
    workspaceNotesRoute,
    noteDetailRoute,
    taskRoute,
    workspaceReportsRoute,
    workspaceSettingsRoute,
    workspacePeopleRoute,
  ]),
  // Top-level routes (no workspace shell). The GitHub install
  // callback lives here because the GitHub App's Setup URL is one
  // fixed URL — it can't carry a workspace slug. The page falls
  // back to `/` if the workspace stash isn't available.
  githubInstallCallbackRoute,
  invitationAcceptRoute,
]);

export const createAppRouter = (queryClient: QueryClient, api: ApiClient) =>
  createRouter({
    routeTree,
    context: { queryClient, api },
    /*
      Preload route components AND data on hover/focus (`intent`). With
      `defaultPreloadDelay: 0` preload fires the instant the cursor enters
      a link — by the time the click lands, both the chunk AND the loader's
      data are warm.
    */
    defaultPreload: 'intent',
    defaultPreloadDelay: 0,
    /*
      No global `defaultPendingComponent` — the workspace layout owns the
      persistent shell, sibling navigations only swap the `<Outlet />`
      content, and the `<TopProgressBar />` provides loading feedback
      without disrupting painted content. `defaultPendingMs: Infinity`
      ensures we never show a viewport-wide skeleton during transitions:
      if a navigation is genuinely slow, the previous page stays painted
      and the progress bar at the top is the only visual signal.
    */
    defaultPendingMs: Number.POSITIVE_INFINITY,
    defaultPendingMinMs: 0,
  });

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
